import { Box, Button, Checkbox, Divider, FormHelperText, Grid, Stack, TextField, Typography } from "@mui/material";
import Alert from "@mui/material/Alert";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import PropTypes from "prop-types";
import React from "react";
import { SELECT_HOURS_LIST, SELECT_MINUTES_LIST, WEEKDAYS_SHORT } from "../../util/constants";
import { sortOnCaseSensitiveTexts } from "../../util/commonUtils";

const BaseSchedule = (props) => {
  const renderHours = () => {
    return SELECT_HOURS_LIST.map((hour) => {
      return (
        <MenuItem key={hour} value={hour}>
          {hour}
        </MenuItem>
      );
    });
  };

  const renderMinutes = () => {
    return SELECT_MINUTES_LIST.map((minute) => {
      return (
        <MenuItem key={minute} value={minute}>
          {minute}
        </MenuItem>
      );
    });
  };

  const renderPositions = () => {
    return props.positions
      .sort((a, b) => sortOnCaseSensitiveTexts(a, b))
      .map((position) => {
        return (
          <MenuItem key={position} value={position}>
            {position}
          </MenuItem>
        );
      });
  };

  return (
    <Grid
      sx={{
        display: "inline-block",
        backgroundColor: "secondary.swaWhite",
        border: `1px solid`,
        borderColor: "secondary.swaGray3",
        borderRadius: "5px",
      }}
      container
    >
      {!!props.baseScheduleErrors.fullScheduleError && (
        <Grid item xs={12}>
          <Alert severity="error">{props.baseScheduleErrors.fullScheduleError}</Alert>
        </Grid>
      )}
      <Grid container sx={{ backgroundColor: "secondary.swaLightestBlue", padding: "10px" }}>
        <Grid item xs={2}>
          <Button
            variant="contained"
            color="primary"
            sx={{
              marginTop: "6px",
            }}
            onClick={props.handleApplyToAll}
          >
            Apply To All
          </Button>
        </Grid>
        <Grid item xs={2} sx={{ marginRight: "30px" }}>
          <Stack direction="row">
            <FormControl
              sx={{
                width: "80px",
                textAlign: "left",
              }}
              variant="standard"
            >
              <InputLabel>Hr</InputLabel>
              <Select
                label="Hr"
                onChange={(event) => props.handleApplyToAllStartHourChange(event.target.value)}
                value={props.applyToAllFilterData.start.hr}
              >
                <MenuItem value="" disabled>
                  <em>Hour</em>
                </MenuItem>
                {renderHours()}
              </Select>
            </FormControl>
            <Box m={1} sx={{ paddingTop: "11px" }}>
              :
            </Box>
            <FormControl
              sx={{
                width: "80px",
                textAlign: "left",
              }}
              variant="standard"
            >
              <InputLabel>Min</InputLabel>
              <Select
                label="Min"
                onChange={(event) => props.handleApplyToAllStartMinChange(event.target.value)}
                value={props.applyToAllFilterData.start.min}
              >
                <MenuItem value="" disabled>
                  <em>Minute</em>
                </MenuItem>
                {renderMinutes()}
              </Select>
            </FormControl>
          </Stack>
        </Grid>
        <Grid item xs={2} sx={{ marginRight: "30px" }}>
          <Stack direction="row">
            <FormControl
              sx={{
                width: "80px",
                textAlign: "left",
              }}
              variant="standard"
            >
              <InputLabel>Hr</InputLabel>
              <Select
                label="Hr"
                onChange={(event) => props.handleApplyToAllEndHourChange(event.target.value)}
                value={props.applyToAllFilterData.end.hr}
              >
                <MenuItem value="" disabled>
                  <em>Hour</em>
                </MenuItem>
                {renderHours()}
              </Select>
            </FormControl>
            <Box m={1} sx={{ paddingTop: "11px" }}>
              :
            </Box>
            <FormControl
              sx={{
                width: "80px",
                textAlign: "left",
              }}
              variant="standard"
            >
              <InputLabel>Min</InputLabel>
              <Select
                label="Min"
                onChange={(event) => props.handleApplyToAllEndMinChange(event.target.value)}
                value={props.applyToAllFilterData.end.min}
              >
                <MenuItem value="" disabled>
                  <em>Minute</em>
                </MenuItem>
                {renderMinutes()}
              </Select>
            </FormControl>
          </Stack>
        </Grid>
        <Grid item xs={3} sx={{ marginRight: "3px", paddingTop: "16px" }}>
          <Stack direction="row">
            <FormControl
              sx={{
                width: "240px",
                textAlign: "left",
              }}
              variant="standard"
            >
              <Select
                label="Position"
                onChange={(event) => props.handleApplyToAllPositionChange(event.target.value)}
                value={props.applyToAllFilterData.position}
                fullWidth
                renderValue={() => props.applyToAllFilterData.position}
              >
                <MenuItem value="" disabled>
                  <em>Position</em>
                </MenuItem>
                {renderPositions()}
                <MenuItem value="Float" disabled hidden={true} />
              </Select>
            </FormControl>
          </Stack>
        </Grid>
        <Grid item xs={2}>
          <Stack direction="row">
            <FormControl
              sx={{
                textAlign: "left",
                paddingTop: "16px",
                width: "200px",
              }}
              variant="standard"
            >
              <TextField
                onChange={(event) => props.handleApplyToAllAssignmentChange(event.target.value)}
                value={props.applyToAllFilterData.assignment}
                variant="standard"
                fullWidth
              ></TextField>
            </FormControl>
          </Stack>
        </Grid>
      </Grid>
      <Grid container sx={{ height: "40px", backgroundColor: "secondary.swaGray2" }}>
        <Grid
          item
          xs={1}
          sx={{ fontWeight: "bold", fontSize: "16px", textAlign: "left", paddingLeft: "18px", paddingTop: "10px" }}
        >
          Off
        </Grid>
        <Grid
          item
          xs={1}
          sx={{ fontWeight: "bold", fontSize: "16px", textAlign: "left", paddingLeft: "10px", paddingTop: "10px" }}
        >
          Day
        </Grid>
        <Grid
          item
          xs={2}
          sx={{ fontWeight: "bold", fontSize: "16px", textAlign: "left", paddingLeft: "5px", paddingTop: "10px" }}
        >
          Start
        </Grid>
        <Grid
          item
          xs={2}
          sx={{ fontWeight: "bold", fontSize: "16px", textAlign: "left", paddingLeft: "32px", paddingTop: "10px" }}
        >
          End
        </Grid>
        <Grid
          item
          xs={2}
          sx={{ fontWeight: "bold", fontSize: "16px", textAlign: "left", paddingLeft: "60px", paddingTop: "10px" }}
        >
          Position
        </Grid>
        <Grid
          item
          xs={2}
          sx={{ fontWeight: "bold", fontSize: "16px", textAlign: "left", paddingLeft: "160px", paddingTop: "10px" }}
        >
          Assignment
        </Grid>
      </Grid>

      {WEEKDAYS_SHORT.map((day) => {
        return (
          <>
            <Grid container key={day} sx={{ padding: "10px" }}>
              <Grid item xs={1}>
                <Checkbox
                  sx={{ paddingLeft: "10px", paddingTop: "14px" }}
                  onChange={(event) => {
                    props.handleCheckedShiftOff(event, day);
                  }}
                  checked={props.baseSchedule[day].isNA ? false : props.baseSchedule[day].isOff}
                  disabled={props.baseSchedule[day].isNA}
                />
              </Grid>

              <Grid item xs={1}>
                <Typography variant="subtitle1" sx={{ paddingTop: "12px" }}>
                  {day.charAt(0).toUpperCase() + day.slice(1).toLowerCase()}
                </Typography>
              </Grid>

              <Grid item xs={2}>
                <Stack direction="row">
                  <FormControl
                    disabled={props.baseSchedule[day].isOff || props.baseSchedule[day].isNA}
                    sx={{
                      width: "80px",
                      textAlign: "left",
                    }}
                    variant="standard"
                    error={
                      !!props.baseScheduleErrors[day].error ||
                      (!!props.baseScheduleErrors.fullScheduleError && !props.baseSchedule[day].isNA)
                    }
                  >
                    <InputLabel>Hr</InputLabel>
                    <Select
                      label="Hr"
                      onChange={(event) => props.handleStartHourChange(day, event.target.value)}
                      value={props.baseSchedule[day].start.hr}
                      renderValue={() =>
                        props.baseSchedule[day].isNA
                          ? "N/A"
                          : props.baseSchedule[day].isOff
                          ? "OFF"
                          : props.baseSchedule[day].start.hr
                      }
                    >
                      <MenuItem value="" disabled>
                        <em>Hour</em>
                      </MenuItem>
                      {renderHours()}
                      <MenuItem value="OFF" disabled hidden={true} />
                      <MenuItem value="N/A" disabled hidden={true} />
                    </Select>
                  </FormControl>
                  <Box m={1} sx={{ paddingTop: "11px" }}>
                    :
                  </Box>
                  <FormControl
                    disabled={props.baseSchedule[day].isOff || props.baseSchedule[day].isNA}
                    sx={{
                      width: "80px",
                      textAlign: "left",
                    }}
                    variant="standard"
                    error={
                      !!props.baseScheduleErrors[day].error ||
                      (!!props.baseScheduleErrors.fullScheduleError && !props.baseSchedule[day].isNA)
                    }
                  >
                    <InputLabel>Min</InputLabel>
                    <Select
                      label="Min"
                      onChange={(event) => props.handleStartMinChange(day, event.target.value)}
                      value={props.baseSchedule[day].start.min}
                      renderValue={() =>
                        props.baseSchedule[day].isNA
                          ? "N/A"
                          : props.baseSchedule[day].isOff
                          ? "OFF"
                          : props.baseSchedule[day].start.min
                      }
                    >
                      <MenuItem value="" disabled>
                        <em>Minute</em>
                      </MenuItem>
                      {renderMinutes()}
                      <MenuItem value="OFF" disabled hidden={true} />
                      <MenuItem value="N/A" disabled hidden={true} />
                    </Select>
                  </FormControl>
                </Stack>
              </Grid>

              <Grid item xs={2} sx={{ paddingLeft: "28px" }}>
                <Stack direction="row">
                  <FormControl
                    disabled={props.baseSchedule[day].isOff || props.baseSchedule[day].isNA}
                    sx={{
                      width: "80px",
                      textAlign: "left",
                    }}
                    variant="standard"
                    error={
                      !!props.baseScheduleErrors[day].error ||
                      (!!props.baseScheduleErrors.fullScheduleError && !props.baseSchedule[day].isNA)
                    }
                  >
                    <InputLabel>Hr</InputLabel>
                    <Select
                      label="Hr"
                      onChange={(event) => props.handleEndHourChange(day, event.target.value)}
                      value={props.baseSchedule[day].end.hr}
                      renderValue={() =>
                        props.baseSchedule[day].isNA
                          ? "N/A"
                          : props.baseSchedule[day].isOff
                          ? "OFF"
                          : props.baseSchedule[day].end.hr
                      }
                    >
                      <MenuItem value="" disabled>
                        <em>Hour</em>
                      </MenuItem>
                      {renderHours()}
                      <MenuItem value="OFF" disabled hidden={true} />
                      <MenuItem value="N/A" disabled hidden={true} />
                    </Select>
                  </FormControl>
                  <Box m={1} sx={{ paddingTop: "11px" }}>
                    :
                  </Box>
                  <FormControl
                    disabled={props.baseSchedule[day].isOff || props.baseSchedule[day].isNA}
                    sx={{
                      width: "80px",
                      textAlign: "left",
                    }}
                    variant="standard"
                    error={
                      !!props.baseScheduleErrors[day].error ||
                      (!!props.baseScheduleErrors.fullScheduleError && !props.baseSchedule[day].isNA)
                    }
                  >
                    <InputLabel>Min</InputLabel>
                    <Select
                      label="Min"
                      onChange={(event) => props.handleEndMinChange(day, event.target.value)}
                      value={props.baseSchedule[day].end.min}
                      renderValue={() =>
                        props.baseSchedule[day].isNA
                          ? "N/A"
                          : props.baseSchedule[day].isOff
                          ? "OFF"
                          : props.baseSchedule[day].end.min
                      }
                    >
                      <MenuItem value="" disabled>
                        <em>Minute</em>
                      </MenuItem>
                      {renderMinutes()}
                      <MenuItem value="OFF" disabled hidden={true} />
                      <MenuItem value="N/A" disabled hidden={true} />
                    </Select>
                  </FormControl>
                </Stack>
              </Grid>

              <Grid item xs={3} sx={{ paddingLeft: "28px" }}>
                <Stack direction="row">
                  <FormControl
                    disabled={props.baseSchedule[day].isOff || props.baseSchedule[day].isNA}
                    sx={{
                      width: "275px",
                      textAlign: "left",
                      paddingTop: "16px",
                      paddingLeft: "31px",
                    }}
                    variant="standard"
                    error={
                      !!props.baseScheduleErrors[day].error ||
                      (!!props.baseScheduleErrors.fullScheduleError && !props.baseSchedule[day].isNA)
                    }
                  >
                    <Select
                      label="Position"
                      onChange={(event) => props.handlePositionChange(day, event.target.value)}
                      value={props.baseSchedule[day].position}
                      renderValue={() =>
                        props.baseSchedule[day].isNA
                          ? "N/A"
                          : props.baseSchedule[day].isOff
                          ? "OFF"
                          : props.baseSchedule[day].position
                      }
                    >
                      <MenuItem value="" disabled>
                        <em>Position</em>
                      </MenuItem>
                      {renderPositions()}
                      <MenuItem value="Float" disabled hidden={true} />
                      <MenuItem value="OFF" disabled hidden={true} />
                      <MenuItem value="N/A" disabled hidden={true} />
                    </Select>
                  </FormControl>
                </Stack>
              </Grid>
              <Grid item xs={3}>
                <Stack direction="row">
                  <FormControl
                    sx={{
                      paddingLeft: "63px",
                      width: "260px",
                      textAlign: "left",
                      paddingTop: "16px",
                    }}
                    variant="standard"
                  >
                    <TextField
                      disabled={props.baseSchedule[day].isOff || props.baseSchedule[day].isNA}
                      onChange={(event) => props.handleAssignmentChange(day, event.target.value)}
                      value={props.baseSchedule[day].assignment}
                      variant="standard"
                      error={
                        !!props.baseScheduleErrors[day].error ||
                        (!!props.baseScheduleErrors.fullScheduleError && !props.baseSchedule[day].isNA)
                      }
                    ></TextField>
                  </FormControl>
                </Stack>
              </Grid>
            </Grid>
            {props.baseScheduleErrors[day].error && (
              <FormHelperText error sx={{ textAlign: "center" }}>
                {props.baseScheduleErrors[day].error}
              </FormHelperText>
            )}
            <Divider />
          </>
        );
      })}
      <Grid>
        <Button
          onClick={props.handleClearBaseSchedule}
          variant="outlined"
          color="primary"
          sx={{ margin: "10px", float: "right" }}
        >
          Clear All
        </Button>
      </Grid>
    </Grid>
  );
};

BaseSchedule.propTypes = {
  baseSchedule: PropTypes.object.isRequired,
  positions: PropTypes.array.isRequired,
  baseScheduleErrors: PropTypes.object.isRequired,
  handleStartHourChange: PropTypes.func.isRequired,
  handleStartMinChange: PropTypes.func.isRequired,
  handleEndHourChange: PropTypes.func.isRequired,
  handleEndMinChange: PropTypes.func.isRequired,
  handleClearBaseSchedule: PropTypes.func.isRequired,
  handleCheckedShiftOff: PropTypes.func.isRequired,
  handleApplyToAll: PropTypes.func.isRequired,
  handleApplyToAllStartHourChange: PropTypes.func.isRequired,
  handleApplyToAllStartMinChange: PropTypes.func.isRequired,
  handleApplyToAllEndHourChange: PropTypes.func.isRequired,
  handleApplyToAllEndMinChange: PropTypes.func.isRequired,
  handlePositionChange: PropTypes.func.isRequired,
  handleAssignmentChange: PropTypes.func.isRequired,
  handleApplyToAllPositionChange: PropTypes.func.isRequired,
  handleApplyToAllAssignmentChange: PropTypes.func.isRequired,
  applyToAllFilterData: PropTypes.object.isRequired,
};

export default BaseSchedule;
